/**
 * Ask ABHI's two sounds, synthesised with Web Audio (no audio files):
 *
 *   'hello'  — a soft rising two-note chime when the page loads.
 *   'ready'  — a quieter single chime when an answer arrives.
 *
 * Browsers block sound until the person has clicked or typed on the page.
 * When 'hello' is blocked (a fresh reload), it waits for the first click
 * or key press in the next few seconds and plays then; after that window
 * it is dropped, so it never sounds on some unrelated click much later.
 * The header's speaker button turns both off; the choice is kept per browser.
 */

const MUTE_KEY = 'askAbhi.muted';
/** How long a blocked 'hello' waits for the first interaction. */
const HELLO_WAIT_MS = 8000;

type Sound = 'hello' | 'ready';

/** Notes per sound: [frequency Hz, start offset s, length s]. */
const NOTES: Record<Sound, [number, number, number][]> = {
  hello: [
    [659.25, 0, 0.5], // E5
    [987.77, 0.09, 0.7], // B5
  ],
  ready: [[880, 0, 0.45]], // A5
};
const VOLUME: Record<Sound, number> = { hello: 0.05, ready: 0.035 };

let ctx: AudioContext | null = null;

function storageGet(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function storageSet(store: Storage | undefined, key: string, value: string | null): void {
  try {
    if (value === null) store?.removeItem(key);
    else store?.setItem(key, value);
  } catch {
    // Private mode / blocked storage — the setting just isn't remembered.
  }
}

export function isChatMuted(): boolean {
  return storageGet(globalThis.localStorage, MUTE_KEY) === '1';
}

export function setChatMuted(muted: boolean): void {
  storageSet(globalThis.localStorage, MUTE_KEY, muted ? '1' : null);
}

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = globalThis.AudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

function canPlayNow(ac: AudioContext): boolean {
  return ac.state === 'running' || navigator.userActivation?.hasBeenActive === true;
}

export function playChatSound(sound: Sound): void {
  if (isChatMuted()) return;
  const ac = audio();
  if (!ac) return;
  if (canPlayNow(ac)) {
    tone(ac, sound);
    return;
  }
  if (sound !== 'hello') return;
  // Blocked until the first interaction: play on it if it comes soon.
  const events = ['pointerdown', 'keydown'] as const;
  const stop = () => {
    clearTimeout(timer);
    events.forEach((e) => window.removeEventListener(e, onFirst, true));
  };
  const onFirst = () => {
    stop();
    if (!isChatMuted()) tone(ac, 'hello');
  };
  const timer = setTimeout(stop, HELLO_WAIT_MS);
  events.forEach((e) => window.addEventListener(e, onFirst, true));
}

function tone(ac: AudioContext, sound: Sound): void {
  void ac.resume().then(() => {
    if (ac.state !== 'running') return;
    const now = ac.currentTime + 0.01;
    for (const [freq, offset, length] of NOTES[sound]) {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = now + offset;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(VOLUME[sound], t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + length);
      osc.connect(gain).connect(ac.destination);
      osc.start(t);
      osc.stop(t + length + 0.05);
    }
  });
}
